/**
 * The dashboard's read cache: stale-while-revalidate, the way GitHub feels.
 *
 * A page that was visited shows what it last knew at once and asks the API
 * again quietly; only data nobody has loaded yet shows a skeleton. Requests
 * for the same key are shared, so two components (or a hover prefetch and
 * the page it leads to) never ask twice, and an answer that did not change
 * keeps its object identity, so nothing that reads it re-renders.
 *
 * What is cached is only what the API said last. It is never invented, it is
 * dropped when the person signs out, and a failure is never hidden: a refresh
 * that fails keeps the last answer on screen *and* raises the stale notice
 * (see useRefreshFailed); an answer that says the data is gone or forbidden
 * (4xx) replaces it with the error.
 */

export interface QueryEntry<T = unknown> {
  data: T | null;
  /** An answer has arrived (even a null one: "no plan yet" is an answer). */
  loaded: boolean;
  /** The error that left this key without data. */
  error: Error | null;
  /** A refresh failed while older data is still shown. */
  refreshError: Error | null;
  fetching: boolean;
  /** When the data was last confirmed by the API (0 = stale, refresh next). */
  at: number;
  seq: number;
}

const EMPTY: QueryEntry = { data: null, loaded: false, error: null, refreshError: null, fetching: false, at: 0, seq: 0 };

/** How long an answer counts as fresh: visiting again within it asks nothing. */
export const DEFAULT_STALE_MS = 1000;
/** Cached keys kept at most; the longest-unused unmounted ones go first. */
const MAX_ENTRIES = 400;

const entries = new Map<string, QueryEntry>();
const listeners = new Map<string, Set<() => void>>();
const loaders = new Map<string, () => Promise<unknown>>();
// When each running request began, and in which epoch (see `epoch`).
const inflight = new Map<string, { at: number; epoch: number }>();
const promises = new Map<string, Promise<void>>();
// A forced refresh asked this soon after a request began, with nothing changed
// since, is that request: asking twice would only double the traffic.
const COALESCE_MS = 300;
// Every successful change bumps this; an answer that was already in flight
// when it happened is stale on arrival and must not be marked fresh.
let epoch = 0;
// Request numbers never repeat, even after an entry is dropped and recreated.
let seqCounter = 0;

function read<T>(key: string): QueryEntry<T> {
  return (entries.get(key) as QueryEntry<T> | undefined) ?? (EMPTY as QueryEntry<T>);
}

function write(key: string, entry: QueryEntry): void {
  entries.set(key, entry);
  listeners.get(key)?.forEach((notify) => notify());
  const had = refreshFailures.has(key);
  if (entry.refreshError) refreshFailures.add(key);
  else refreshFailures.delete(key);
  if (had !== Boolean(entry.refreshError)) notifyFailures();
  if (entries.size > MAX_ENTRIES) evict();
}

function evict(): void {
  const candidates = [...entries.entries()].filter(([key]) => !(listeners.get(key)?.size));
  candidates.sort((a, b) => a[1].at - b[1].at);
  for (const [key] of candidates.slice(0, entries.size - MAX_ENTRIES)) entries.delete(key);
}

/** Reuse every part of `next` that equals `prev`, so unchanged data keeps its identity. */
export function replaceEqualDeep<T>(prev: unknown, next: T): T {
  if (prev === (next as unknown)) return next;
  if (Array.isArray(prev) && Array.isArray(next)) {
    let same = prev.length === next.length;
    const merged = next.map((item, index) => {
      const value = replaceEqualDeep(prev[index], item);
      if (value !== prev[index]) same = false;
      return value;
    });
    return (same ? prev : merged) as T;
  }
  if (isPlainObject(prev) && isPlainObject(next)) {
    const keys = Object.keys(next);
    let same = Object.keys(prev).length === keys.length;
    const merged: Record<string, unknown> = {};
    for (const name of keys) {
      const value = replaceEqualDeep(prev[name], next[name]);
      merged[name] = value;
      if (value !== prev[name]) same = false;
    }
    return (same ? prev : merged) as T;
  }
  return next;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && Object.getPrototypeOf(value) === Object.prototype;
}

/** A 4xx answer is the API saying the data is gone or not yours: never keep showing the old. */
function isAuthoritative(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  return typeof status === 'number' && status >= 400 && status < 500 && status !== 408 && status !== 429;
}

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

export interface RunOptions {
  /** Ask again even if an answer is fresh or on its way (after a change, or a reload). */
  force?: boolean;
  staleMs?: number;
}

/** Load `key` through `loader` unless a fresh answer is there or one is already coming. */
export function run<T>(key: string, loader: () => Promise<T>, options: RunOptions = {}): Promise<void> {
  const current = read<T>(key);
  const running = inflight.get(key);
  if (options.force && current.fetching && running && running.epoch === epoch && Date.now() - running.at < COALESCE_MS) {
    return promises.get(key) ?? Promise.resolve();
  }
  if (!options.force) {
    if (current.fetching) return promises.get(key) ?? Promise.resolve();
    const fresh = current.loaded && current.at > 0 && Date.now() - current.at < (options.staleMs ?? DEFAULT_STALE_MS);
    if (fresh) return Promise.resolve();
  }
  const seq = ++seqCounter;
  const startedInEpoch = epoch;
  inflight.set(key, { at: Date.now(), epoch });
  write(key, { ...current, fetching: true, seq });
  const promise = loader().then(
    (result) => {
      const now = read<T>(key);
      if (now.seq !== seq) return;
      inflight.delete(key);
      write(key, {
        data: replaceEqualDeep(now.data, result),
        loaded: true,
        error: null,
        refreshError: null,
        fetching: false,
        at: epoch === startedInEpoch ? Date.now() : 0,
        seq,
      });
    },
    (cause: unknown) => {
      const now = read<T>(key);
      if (now.seq !== seq) return;
      inflight.delete(key);
      const error = toError(cause);
      if (now.loaded && !isAuthoritative(error)) {
        write(key, { ...now, fetching: false, refreshError: error });
      } else {
        write(key, { data: null, loaded: false, error, refreshError: null, fetching: false, at: 0, seq });
      }
    },
  );
  promises.set(key, promise);
  void promise.then(() => {
    if (promises.get(key) === promise) promises.delete(key);
  });
  return promise;
}

/** Put a known answer in (after a save returns the new state): shown now, confirmed on the next refresh. */
export function setData<T>(key: string, data: T): void {
  const current = read<T>(key);
  write(key, { ...current, data: replaceEqualDeep(current.data, data), loaded: true, error: null, refreshError: null, at: Date.now(), seq: ++seqCounter });
}

/** Warm `key` ahead of the visit (hover, focus, touch); a fresh answer is left alone. */
export function prefetch<T>(key: string, loader: () => Promise<T>, staleMs?: number): void {
  void run(key, loader, { staleMs });
}

export function peek<T>(key: string): QueryEntry<T> {
  return read<T>(key);
}

export function subscribe(key: string, notify: () => void): () => void {
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(notify);
  if (refreshFailures.has(key)) notifyFailures();
  return () => {
    set!.delete(notify);
    if (set!.size === 0) listeners.delete(key);
    if (refreshFailures.has(key)) notifyFailures();
  };
}

/** The mounted component's way to refresh its key later (events, focus, retry). */
export function registerLoader(key: string, loader: () => Promise<unknown>): () => void {
  loaders.set(key, loader);
  return () => {
    if (loaders.get(key) === loader) loaders.delete(key);
  };
}

/** Forget an entry once nothing reads it any more. */
export function dropIfUnread(key: string): void {
  if (!listeners.get(key)?.size) drop(key);
}

/** Forget an entry nobody keeps (an unkeyed resource whose component left). */
export function drop(key: string): void {
  entries.delete(key);
  loaders.delete(key);
  inflight.delete(key);
  promises.delete(key);
  if (refreshFailures.delete(key)) notifyFailures();
}

/**
 * A change was made: everything cached is now possibly out of date. Nothing
 * is removed or refetched here — what is on screen stays — but the next visit
 * to any key asks the API again instead of trusting the cache.
 */
export function markAllStale(): void {
  epoch += 1;
  for (const [key, entry] of entries) {
    if (entry.at !== 0) entries.set(key, { ...entry, at: 0 });
  }
}

let pending: ReturnType<typeof setTimeout> | null = null;
const pendingKeys = new Set<string>();

/**
 * Something changed on the server (a live event): refresh the mounted keys
 * `matches` selects — quietly, a burst of events once — and mark the rest of
 * the matching keys stale for their next visit.
 */
export function invalidate(matches: (key: string) => boolean): void {
  epoch += 1;
  for (const [key, entry] of entries) {
    if (!matches(key)) continue;
    if (entry.at !== 0) entries.set(key, { ...entry, at: 0 });
    if (loaders.has(key)) pendingKeys.add(key);
  }
  if (pending || pendingKeys.size === 0) return;
  pending = setTimeout(() => {
    pending = null;
    const keys = [...pendingKeys];
    pendingKeys.clear();
    for (const key of keys) {
      const loader = loaders.get(key);
      if (loader) void run(key, loader, { force: true });
    }
  }, 150);
}

/** Refresh every mounted key that has not been confirmed for a while (coming back to the tab, Retry). */
export function revalidateMounted(olderThanMs = 0): void {
  const now = Date.now();
  for (const [key, loader] of loaders) {
    const entry = read(key);
    if (entry.fetching) continue;
    if (olderThanMs > 0 && entry.at !== 0 && now - entry.at < olderThanMs) continue;
    void run(key, loader, { force: true });
  }
}

/**
 * Sign-out, or a different person: nothing one session saw may reach the
 * next. This forgets what was *cached*. A component's own private entry
 * (`~…`, see useResource) is working state of whatever is on screen right now,
 * not something a session saw, so it stays: the sign-in page that calls this
 * is rendered by a guard whose own read must survive.
 */
export function clearAll(): void {
  epoch += 1;
  for (const key of [...entries.keys()]) {
    if (key.startsWith('~')) continue;
    entries.delete(key);
    inflight.delete(key);
    promises.delete(key);
    pendingKeys.delete(key);
    refreshFailures.delete(key);
  }
  notifyFailures();
  for (const [key, set] of listeners) {
    if (!key.startsWith('~')) set.forEach((notify) => notify());
  }
}

// --- the stale notice: which keys last failed to refresh --------------------

const refreshFailures = new Set<string>();
const failureListeners = new Set<() => void>();
let failureCount = 0;

function notifyFailures(): void {
  // Only what is on screen: a page nobody is looking at has nothing to apologise for.
  const count = [...refreshFailures].filter((key) => listeners.get(key)?.size).length;
  if (count === failureCount) return;
  failureCount = count;
  failureListeners.forEach((notify) => notify());
}

export function subscribeFailures(notify: () => void): () => void {
  failureListeners.add(notify);
  return () => failureListeners.delete(notify);
}

/** How many keys on screen are showing older data because a refresh failed. */
export function failureSnapshot(): number {
  return failureCount;
}

if (typeof document !== 'undefined') {
  // Coming back to the tab: whatever is on screen may have moved on.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') revalidateMounted(15_000);
  });
}
